/**
 * Keeping a world stocked when the world has no edges.
 *
 * A map that exists all at once can be stocked all at once: scatter fourteen
 * head over the island, drop forty карасі wherever there is water, done. An
 * adventure cannot. It is a thousand blocks across and only the couple of
 * hundred round the player have been grown, so scattering over the whole of
 * it puts nothing anywhere - it is four thousand tries at finding water in
 * country that does not exist yet.
 *
 * So the life follows the player: what falls a long way behind is taken away,
 * what is missing is put down in the country ahead. The rules for *where*
 * live here rather than in the game loop, because "is this somewhere a pig
 * can stand" is a question with a right answer and no need of a browser.
 */

/** How far from the player an adventure keeps its animals and its fish. */
export const LIFE_KEEP = 150;
/** And the ring it puts new ones in: far enough not to appear underfoot. */
export const LIFE_NEAR = 40;
export const LIFE_PUT = 90;
/**
 * How much of each there is.
 *
 * These are what a walk meets at any moment rather than what exists in the
 * world, and the first numbers were the кремль's - fourteen head, which is
 * what one island holds. Spread over a ring a hundred and thirty blocks
 * wide that is one animal per three thousand square metres, and a walk met
 * one свиня and one тур, which is what was reported.
 *
 * Forty-four over a ring ninety wide is one per six hundred, which is a
 * country with animals in it rather than a country with an animal in it.
 */
export const LIFE_HERD = 44;
export const LIFE_FISH = 30;

/** What this needs to know about the world. Enough to look at one cell. */
export interface Ground {
  /** The highest solid block of this column, or below zero for none. */
  groundAt(x: number, z: number): number;
  /** What is at this cell. */
  blockAt(x: number, y: number, z: number): number;
  /** Is this cell water, or anything else you can swim in? */
  fluidAt(x: number, y: number, z: number): boolean;
  /** Is this column inside the map at all? */
  inside(x: number, z: number): boolean;
}

/** Somewhere to put something: a column, without a height yet. */
export interface Spot {
  readonly x: number;
  readonly z: number;
}

/**
 * Has this wandered too far behind to be worth keeping?
 *
 * Square rather than round, to match the country: what gets thrown away is a
 * square of columns round the player, and a beast standing in a column that
 * no longer exists would be standing in the sky.
 */
export function strayed(
  e: { x: number; z: number }, px: number, pz: number, keep: number,
): boolean {
  return Math.max(Math.abs(e.x - px), Math.abs(e.z - pz)) > keep;
}

/**
 * A column in the ring around somebody.
 *
 * Never underfoot: something that appears four blocks away appeared, and the
 * whole point is that it should have been there all along. `roll` is the
 * source of chance, passed in so a check can hand it a known sequence.
 */
export function ringSpot(
  px: number, pz: number, near: number, far: number,
  roll: () => number,
): Spot {
  const angle = roll() * Math.PI * 2;
  const away = near + roll() * (far - near);
  return {
    x: Math.round(px + Math.cos(angle) * away),
    z: Math.round(pz + Math.sin(angle) * away),
  };
}

/**
 * Can a beast stand here?
 *
 * Dry ground with two clear cells over it, and no closer to the water than
 * that: an animal put down on the last dry block of a shore is one step from
 * the sea, and the first herd spawned that way had nine of fourteen swimming
 * inside a minute.
 *
 * A column nobody has grown yet answers no, and that falls out of the same
 * test: an ungrown column is air all the way down and `groundAt` says -1 for
 * it, which is below any waterline there has ever been. Worth saying out
 * loud, because it is the failure that would not look like one - a свиня
 * placed in country that does not exist stands on nothing and falls out of
 * the world, silently, a hundred blocks behind you.
 *
 * @returns the height to stand it at, or null.
 */
export function standingRoom(
  world: Ground, at: Spot, seaLevel: number, air: number,
): number | null {
  const ground = world.groundAt(at.x, at.z);
  if (ground <= seaLevel + 1) return null;
  return footroom(world, at, air);
}

/**
 * Two clear cells over dry ground, and nothing about the sea.
 *
 * The half of `standingRoom` that is about whether a body fits. Kept apart
 * because the shoreline rule above is a rule about *animals* - a свиня put
 * down on the last dry block of a beach is a свиня one step from drowning -
 * and it is exactly wrong for a мавка, whose whole business is standing at
 * the water's edge at night. Asking `standingRoom` for one found seven
 * places in four hundred tries along a shore, all of them further inland
 * than she is supposed to be.
 *
 * @returns the height to stand at, or null.
 */
export function footroom(
  world: Ground, at: Spot, air: number,
): number | null {
  if (!world.inside(at.x, at.z)) return null;
  const ground = world.groundAt(at.x, at.z);
  if (ground < 0) return null;
  // Dry: what a body stands on must not be the surface of a lake.
  if (world.fluidAt(at.x, ground + 1, at.z)) return null;
  if (world.blockAt(at.x, ground + 1, at.z) !== air) return null;
  if (world.blockAt(at.x, ground + 2, at.z) !== air) return null;
  return ground + 1;
}

/**
 * Is there water enough here for a fish to turn round in?
 *
 * Two cells of water, the lower one just off the bed. It used to start
 * looking a cell higher, which quietly demanded three - and the lakes this
 * world makes are two deep more often than not, so whole ponds counted as
 * dry. A карась in two blocks of water is a карась in a pond.
 *
 * Asked of the water and not of the waterline. This used to refuse any bed
 * standing above sea level, as a cheap way of skipping the dry country - and
 * then the adventure grew lakes, which are all above sea level by
 * construction, and it refused every one of them. The карасі were put down
 * at a lakeside start, beside a lake, and not one of them was in it.
 *
 * @returns the height to swim at, or null.
 */
export function swimmingRoom(world: Ground, at: Spot): number | null {
  if (!world.inside(at.x, at.z)) return null;
  const bed = world.groundAt(at.x, at.z);
  if (bed < 0) return null;
  const y = bed + 1;
  if (!world.fluidAt(at.x, y, at.z)) return null;
  if (!world.fluidAt(at.x, y + 1, at.z)) return null;
  return y;
}

// --- what the adventure keeps in its woods and its lakes ---------------------

/**
 * How many вовкулаки and мавки a walk meets, and where they are put.
 *
 * Far fewer than the stock, and further off: an animal that appears ninety
 * blocks away is a pig you will find later, and a вовкулака that appears
 * ninety blocks away is a вовкулака that has time to find *you*. Ten at a
 * time over a ring seventy blocks wide is roughly one every four hundred
 * square metres of wood - a wood with a thing in it rather than a wood made
 * of things.
 */
export const FOES_KEEP = 120;
export const FOE_NEAR = 26;
export const FOE_PUT = 70;
export const FOES_WOOD = 6;
export const FOES_WATER = 4;

/** What deciding where a вовкулака goes needs to know. */
export interface Wood extends Ground {
  /** Is this cell a leaf of some tree? */
  leafAt(x: number, y: number, z: number): boolean;
}

/**
 * Is this spot in a wood?
 *
 * Asked of the leaves over the ground rather than of the noise that planted
 * them, because the question a player would ask standing there is "can I see
 * the sky" and the answer to that is in the blocks. It also means a wood
 * somebody has cleared stops being a wood, which is the right answer and
 * would have taken a whole extra system to get any other way.
 */
export function inWood(
  world: Wood, at: Spot, seaLevel: number, air: number, reach = 16,
): number | null {
  const y = standingRoom(world, at, seaLevel, air);
  if (y === null) return null;
  for (let d = 1; d <= reach; d++) {
    if (world.leafAt(at.x, y + d, at.z)) return y;
  }
  return null;
}

/**
 * Is this spot at the edge of water, where something could come out of it?
 *
 * Dry ground to stand on with water within a step or two. A мавка standing
 * in the middle of a lake would be swimming, and one put down in a meadow
 * did not come out of anywhere.
 */
export function atTheWater(
  world: Ground, at: Spot, seaLevel: number, air: number, reach = 3,
): number | null {
  void seaLevel;
  const y = footroom(world, at, air);
  if (y === null) return null;
  for (let dx = -reach; dx <= reach; dx++) {
    for (let dz = -reach; dz <= reach; dz++) {
      if (dx === 0 && dz === 0) continue;
      const bed = world.groundAt(at.x + dx, at.z + dz);
      if (bed < 0) continue;
      if (world.fluidAt(at.x + dx, bed + 1, at.z + dz)) return y;
    }
  }
  return null;
}

// --- the whole of it, in one place ------------------------------------------

/**
 * Somebody the country is kept stocked around.
 *
 * A list of them, not one: alone there is one entry, and on a server there
 * are several standing in different parts of the map. Everything below is
 * written for the list, because "keep the country round the people stocked"
 * with one caller passing one person and another passing five is the same
 * question asked once - and the single-player version of it had already been
 * written twice by the time this was.
 */
export interface Person {
  readonly x: number;
  readonly z: number;
}

/** Has this wandered too far behind *everybody*? */
export function strayedFromAll(
  e: { x: number; z: number }, people: readonly Person[], keep: number,
): boolean {
  return people.every((p) => strayed(e, p.x, p.z, keep));
}

/** What a restock decided to put down, and where. */
export interface Placement {
  readonly what: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** What is already about, so the rules know what is missing. */
export interface Stock {
  /** How many animals are within `LIFE_KEEP` of this person. */
  readonly beasts: number;
  readonly fish: number;
  /** And how many of each wild foe, by kind. */
  readonly wolves: number;
  readonly mavkas: number;
  /**
   * How many animals there are in the world altogether.
   *
   * Only used to pick *which* animal comes next, so that a walk meets two
   * hens to a pig to a тур rather than eight of whatever the counter happens
   * to be on. It is deliberately not what decides *whether* one is put down:
   * that is the count near this person, or a server with three people in
   * three countries would stock the first and leave the other two empty.
   */
  readonly turn: number;
}

/**
 * What to put down around one person, this tick.
 *
 * Pure, and it decides nothing about *making* anything: it hands back a list
 * of "a свиня, here", and the caller - which is the only thing that knows
 * what a свиня is made of on its side of the wire - builds them. That is what
 * lets the lone game and the server run the same rules without the server
 * importing a renderer or the page importing a realm.
 *
 * A few a tick at most, and that is the whole of the pacing: filling a quota
 * in one go is a visible stutter, and a herd that assembles over five seconds
 * while you walk reads as animals wandering in rather than as a spawn.
 */
export function whatToPutDown(
  world: Wood,
  who: Person,
  have: Stock,
  seaLevel: number,
  air: number,
  /** Between dusk and dawn: the мавки are out, and nothing else changes. */
  dark: boolean,
  roll: () => number = Math.random,
): Placement[] {
  const out: Placement[] = [];
  const ringLife = (): Spot => ringSpot(who.x, who.z, LIFE_NEAR, LIFE_PUT, roll);
  const ringFoes = (): Spot => ringSpot(who.x, who.z, FOE_NEAR, FOE_PUT, roll);

  let put = 0;
  for (let tries = 0; tries < 120 && have.beasts + put < LIFE_HERD && put < 8;
    tries++) {
    const at = ringLife();
    const y = standingRoom(world, at, seaLevel, air);
    if (y === null) continue;
    /*
     * Two hens to a pig to a тур, and one in eight a собака or a кіт.
     *
     * Which is roughly what a подвір'я holds: birds are what there are most
     * of, cattle what there are fewest of, and a country with as many
     * aurochs in it as chickens is a country nobody has ever been to. The
     * dogs and cats are fewer still, because a dog in every field is a dog
     * nobody is pleased to find.
     */
    const turn = (have.turn + put) % 8;
    out.push({
      what: turn === 0 ? "tur" : turn === 1 || turn === 5 ? "pig"
        : turn === 3 ? "sobaka" : turn === 7 ? "kit" : "kurka",
      x: at.x + 0.5, y, z: at.z + 0.5,
    });
    put++;
  }

  let caught = 0;
  for (let tries = 0; tries < 120 && have.fish + caught < LIFE_FISH && caught < 8;
    tries++) {
    const at = ringLife();
    const y = swimmingRoom(world, at);
    if (y === null) continue;
    out.push({ what: "karas", x: at.x + 0.5, y: y + 0.5, z: at.z + 0.5 });
    caught++;
  }

  /*
   * And many more tries for the things that live in the woods.
   *
   * A pig can stand almost anywhere; a вовкулака needs country with a canopy
   * over it, and that is about two hundredths of an adventure - seventeen of
   * nine hundred tries, measured in a ring round a walked patch. Forty tries
   * would have put one down about once a minute, which is a feature that
   * looks exactly like a feature that does not work.
   */
  let denned = 0;
  for (let tries = 0; tries < 250 && denned < 2 &&
    have.wolves + denned < FOES_WOOD; tries++) {
    const at = ringFoes();
    const y = inWood(world, at, seaLevel, air);
    if (y === null) continue;
    out.push({ what: "vovkulaka", x: at.x + 0.5, y, z: at.z + 0.5 });
    denned++;
  }

  // The мавка is out at the water day and night: the green one by day,
  // who keeps company, the red-eyed one after dark, who does not.
  let risen = 0;
  for (let tries = 0; tries < 250 && risen < 2 &&
    have.mavkas + risen < FOES_WATER; tries++) {
    const at = ringFoes();
    const y = atTheWater(world, at, seaLevel, air);
    if (y === null) continue;
    out.push({ what: dark ? "mavkaNight" : "mavka", x: at.x + 0.5, y, z: at.z + 0.5 });
    risen++;
  }

  return out;
}
